import { ChevronDownIcon } from '@chakra-ui/icons';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useCurrentUserContext } from '@/contexts';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useOwner } from '@/features/permissions/hooks/useOwner';
import type { Collaborator } from '@/features/permissions/types';
import { Role } from '@/types/gql.generated';
import { useToast } from '@/ui';
import {
  Avatar,
  Box,
  Button,
  Flex,
  type FlexProps,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  useDisclosure,
} from '@/ui';
import { useTransferOwnership } from '../../../../hooks/useTransferOwnership';
import { LeaveConfirmation } from '../../LeaveConfirmation';

type Props = Omit<FlexProps, 'role'> & {
  collaborator: Collaborator;
  onRemove: (user: Collaborator) => void;
  onChangeRole: (user: Collaborator, role: Role) => void;
};

const roleLabelMap: Record<Role, string | null> = {
  [Role.Owner]: 'Owner',
  [Role.Editor]: 'Editor',
  [Role.Viewer]: 'Viewer',
  [Role.Public]: null,
};

export const UserRow = ({
  collaborator,
  onRemove,
  onChangeRole,
  ...flexProps
}: Props) => {
  const { experience } = usePageExperienceContext();
  const { currentUser } = useCurrentUserContext();
  const { isOwnerMe } = useOwner();
  const toast = useToast();
  const { transferOwnership, isPending: isTransferringOwnership } =
    useTransferOwnership();
  const isMe = currentUser?.id === collaborator.id;
  const isOwner = collaborator.role === Role.Owner;

  const {
    isOpen: isLeaveConfirmOpen,
    onOpen: openLeaveConfirm,
    onClose: closeLeaveConfirm,
  } = useDisclosure();

  const {
    isOpen: isRemoveConfirmOpen,
    onOpen: openRemoveConfirm,
    onClose: closeRemoveConfirm,
  } = useDisclosure();

  const {
    isOpen: isTransferConfirmOpen,
    onOpen: openTransferConfirm,
    onClose: closeTransferConfirm,
  } = useDisclosure();

  const name = collaborator.name || collaborator.email;

  return (
    <>
      <LeaveConfirmation
        isOpen={isLeaveConfirmOpen}
        onCancel={closeLeaveConfirm}
      />

      <ConfirmDialog
        confirmButtonText="Remove"
        isOpen={isRemoveConfirmOpen}
        title={`Remove ${name} from ${experience?.title}?`}
        onCancel={closeRemoveConfirm}
        onConfirm={() => onRemove(collaborator)}
      />

      <ConfirmDialog
        confirmButtonText="Yes, transfer ownership"
        isLoading={isTransferringOwnership}
        isOpen={isTransferConfirmOpen}
        message="The new owner will be notified and could remove you from this page. You also might lose the ability to change share settings."
        title={`Make ${collaborator.name || collaborator.email} the owner?`}
        onCancel={closeTransferConfirm}
        onConfirm={() => {
          transferOwnership(collaborator.id, {
            onError: () => toast.error('Error transferring ownership'),
            onSuccess: () => {
              closeTransferConfirm();
              toast.notify('Ownership transferred');
            },
          });
        }}
      />

      <Flex align="center" {...flexProps}>
        <Avatar
          mr="4"
          name={collaborator.name || collaborator.email || ''}
          size="sm"
          src={collaborator.avatar ?? undefined}
        />
        <Box flex="1" lineHeight="normal">
          <Text fontWeight="medium">
            {collaborator.name || collaborator.email} {isMe && '(you)'}
          </Text>
          {collaborator.name && (
            <Text color="customgray.mid" fontSize="sm">
              {collaborator.email}
            </Text>
          )}
        </Box>
        {isOwner ? (
          <Text fontWeight="medium">{roleLabelMap[collaborator.role]}</Text>
        ) : (
          <Menu isLazy placement="bottom-end">
            <MenuButton
              as={Button}
              colorScheme="dark"
              fontSize="md"
              mr="-4"
              rightIcon={<ChevronDownIcon />}
              size="sm"
              variant="ghost"
            >
              {roleLabelMap[collaborator.role]}
            </MenuButton>
            <MenuList minW="9rem">
              {/* Editors can't change their own permissions */}
              {!isMe && (
                <>
                  <MenuOptionGroup
                    type="radio"
                    value={collaborator.role}
                    onChange={(value) => {
                      onChangeRole(collaborator, value as Role);
                    }}
                  >
                    <MenuItemOption value={Role.Viewer}>Viewer</MenuItemOption>
                    <MenuItemOption value={Role.Editor}>Editor</MenuItemOption>
                  </MenuOptionGroup>
                  <MenuDivider />
                </>
              )}
              {isMe ? (
                <MenuItem onClick={openLeaveConfirm}>Leave page</MenuItem>
              ) : (
                <MenuItem onClick={openRemoveConfirm}>Remove access</MenuItem>
              )}
              {/* Only owners can transfer ownership to another member */}
              {isOwnerMe && (
                <MenuItem onClick={openTransferConfirm}>
                  Transfer ownership
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        )}
      </Flex>
    </>
  );
};
